export const looksLikeMonthOffsets = (xs: unknown[]): xs is number[] => {
  if (!Array.isArray(xs) || xs.length === 0) return false

  const everyItemIsInteger = xs.every(
    value => Number.isFinite(value as number) && Math.floor(value as number) === value,
  )

  if (!everyItemIsInteger) return false

  const largestValue = Math.max(...(xs as number[]))
  return largestValue === 0
}

export const monthLabelsFromOffsets = (baseIsoDate: string, monthOffsets: number[]): string[] => {
  const baseMatch = baseIsoDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
  const baseDate = baseMatch
    ? new Date(Date.UTC(Number(baseMatch[1]), Number(baseMatch[2]) - 1, 1))
    : new Date()

  const monthYearFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  })

  return monthOffsets.map(offset => {
    const monthDate = new Date(
      Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth() + offset, 1),
    )

    const parts = monthYearFormatter
      .format(monthDate)
      .replace(/\./g, '')
      .split(/\s+de\s+|\s+/)
      .filter(piece => piece && piece.toLowerCase() !== 'de')

    const monthLabel = (parts[0] ?? '').toLowerCase()
    const yearTwoDigits = parts[1] ?? ''
    return `${monthLabel}/${yearTwoDigits}`
  })
}

export function normalizeWeekLabels(
  rawLabels: unknown[],
  options?: { withYear?: boolean; refStart?: string },
): string[] {
  const includeYearInLabel = options?.withYear ?? true

  const referenceYear: number = (() => {
    const referenceStartString = options?.refStart
    const match = referenceStartString?.match(/^(\d{4})-/)
    return match ? Number(match[1]) : new Date().getUTCFullYear()
  })()

  const toTwoDigitYear = (year: number) => String(year).slice(-2)

  const safeArray = Array.isArray(rawLabels) ? rawLabels : []

  return safeArray.map(value => {
    const asText = String(value).trim()

    let match = asText.match(/^(\d{4})[-/Ww]?(\d{1,2})$/)
    if (match) {
      const fullYear = Number(match[1])
      const weekNumber = Number(match[2])
      return includeYearInLabel
        ? `sem ${weekNumber}/${toTwoDigitYear(fullYear)}`
        : `sem ${weekNumber}`
    }

    match = asText.match(/^(\d{1,2})[-/](\d{4})$/)
    if (match) {
      const weekNumber = Number(match[1])
      const fullYear = Number(match[2])
      return includeYearInLabel
        ? `sem ${weekNumber}/${toTwoDigitYear(fullYear)}`
        : `sem ${weekNumber}`
    }
    match = asText.match(/^\s*sem\.?\s*(\d{1,2})/i)
    if (match) {
      const weekNumber = Number(match[1])
      return includeYearInLabel
        ? `sem ${weekNumber}/${toTwoDigitYear(referenceYear)}`
        : `sem ${weekNumber}`
    }

    match = asText.match(/^\d{1,2}$/)
    if (match) {
      const weekNumber = Number(asText)
      return includeYearInLabel
        ? `sem ${weekNumber}/${toTwoDigitYear(referenceYear)}`
        : `sem ${weekNumber}`
    }

    // Fallback
    return asText.toLowerCase().startsWith('sem') ? asText : `sem ${asText}`
  })
}

export function buildWeekTicks(totalPoints: number, normalizedLabels: string[]) {
  if (totalPoints <= 12) {
    const tickValues = Array.from({ length: totalPoints }, (_unused, index) => index + 1)
    return { tickvals: tickValues, ticktext: normalizedLabels }
  }

  const desiredLabelCount = 8
  const step = Math.max(1, Math.round(totalPoints / desiredLabelCount))

  const tickValues: number[] = []
  for (let index = 1; index <= totalPoints; index += step) {
    tickValues.push(index)
  }

  if (tickValues[tickValues.length - 1] !== totalPoints) {
    tickValues.push(totalPoints)
  }

  const tickTexts = tickValues.map(value => normalizedLabels[value - 1])
  return { tickvals: tickValues, ticktext: tickTexts }
}
